import './style.css';

/* 
23/02/2026 16h
Adiciodo clicks ao titulo para ordenação e suporte a colunas numéricas com formatação 
  (ex: "1.234,56" ou "1,234.56"), detectando automaticamente o formato mais comum.
Permite customização total de células via objetos, mantendo compatibilidade com uso antigo.
*/

import React, { useState, useRef } from 'react';

const TABLE = ({ header, body, foot, onRowClick }) => {
    const [sortConfig, setSortConfig] = useState({ col: null, direction: null });
    const [filterConfig, setFilterConfig] = useState({});
    const [sortedBody, setSortedBody] = useState(body);
    const [filterPopup, setFilterPopup] = useState({
        open: false,
        colIdx: null,
        x: 0,
        y: 0,
        value: ''
    });
    const originalBody = useRef(body);
    const longPressTimerRef = useRef(null);
    const suppressSortClickRef = useRef(false);

    const getCellValue = (cell) => {
        let value = cell;
        if (value && typeof value === 'object' && 'content' in value) {
            value = value.content;
        }
        if (value === undefined || value === null) return '';
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return value;
        }
        return '';
    };

    const applyFilter = (source, filters) => {
        if (!filters || Object.keys(filters).length === 0) return source;
        return (source || []).filter(row => {
            return Object.entries(filters).every(([colKey, rawFilter]) => {
                const colIdx = Number(colKey);
                const filterValue = String(rawFilter || '').trim().toLowerCase();
                if (!filterValue) return true;
                const cellValue = String(getCellValue(row[colIdx])).toLowerCase();
                return cellValue.includes(filterValue);
            });
        });
    };

    const applySort = (source, config) => {
        if (!config || config.col === null || !config.direction) return source;
        const colIdx = config.col;
        const direction = config.direction;
        const numeric = isNumericColumn(colIdx, source);

        return [...source].sort((a, b) => {
            let aVal = getCellValue(a[colIdx]);
            let bVal = getCellValue(b[colIdx]);
            if (numeric) {
                const aNum = parseMaskedNumber(aVal);
                const bNum = parseMaskedNumber(bVal);
                if (isNaN(aNum) && isNaN(bNum)) return 0;
                if (isNaN(aNum)) return 1;
                if (isNaN(bNum)) return -1;
                return direction === 'asc' ? aNum - bNum : bNum - aNum;
            }
            if (aVal === undefined || aVal === null) return 1;
            if (bVal === undefined || bVal === null) return -1;
            return direction === 'asc'
                ? String(aVal).localeCompare(String(bVal))
                : String(bVal).localeCompare(String(aVal));
        });
    };

    const getProcessedBody = (source, filters, config) => {
        const filtered = applyFilter(source || [], filters);
        return applySort(filtered, config);
    };

    React.useEffect(() => {
        originalBody.current = body;
        setSortedBody(getProcessedBody(body, filterConfig, sortConfig));
    }, [body]);

    // Função auxiliar para renderizar uma célula
    const renderCell = (cell, idx, isHeader = false, headerEvents = null, sortState = null, isFiltered = false, isSortable = true) => {
        const Tag = isHeader ? 'th' : 'td';
        let extraProps = {};
        if (isHeader && headerEvents && isSortable) {
            extraProps.onClick = headerEvents.onClick;
            extraProps.onContextMenu = headerEvents.onContextMenu;
            extraProps.onTouchStart = headerEvents.onTouchStart;
            extraProps.onTouchEnd = headerEvents.onTouchEnd;
            extraProps.onTouchMove = headerEvents.onTouchMove;
            extraProps.style = { cursor: 'pointer', ...(cell && cell.style ? cell.style : {}) };
        } else if (cell && cell.style) {
            extraProps.style = cell.style;
        }
        if (cell && typeof cell === 'object' && ('content' in cell)) {
            return (
                <Tag
                    key={idx}
                    className={cell.className || ''}
                    {...(cell.props || {})}
                    {...extraProps}
                >
                    {cell.content}
                    {isHeader && (
                        <span style={{ marginLeft: 4 }}>
                            {sortState === 'asc' ? '▲' : sortState === 'desc' ? '▼' : ''}
                            {isFiltered ? ' 🔎' : ''}
                        </span>
                    )}
                </Tag>
            );
        }
        return (
            <Tag key={idx} {...extraProps}>
                {cell}
                {isHeader && (
                    <span style={{ marginLeft: 4 }}>
                        {sortState === 'asc' ? '▲' : sortState === 'desc' ? '▼' : ''}
                        {isFiltered ? ' 🔎' : ''}
                    </span>
                )}
            </Tag>
        );
    };

    // Função para ordenar
    // Função para detectar se a coluna é numérica
    // Função para limpar máscara e converter para número
    const parseMaskedNumber = (val) => {
        if (typeof val !== 'string') return val;
        // Remove espaços, separador de milhar e troca vírgula por ponto se necessário
        let clean = val.replace(/\s/g, '').replace(/\.(?=\d{3,})/g, '');
        // Se tem vírgula e não tem ponto, vírgula é decimal (pt-BR)
        if (clean.indexOf(',') > -1 && clean.indexOf('.') === -1) {
            clean = clean.replace(',', '.');
        } else if (clean.indexOf('.') > -1 && clean.indexOf(',') > -1) {
            // Se tem ambos, remove pontos (milhar), troca vírgula por ponto
            clean = clean.replace(/\./g, '').replace(',', '.');
        }
        return parseFloat(clean);
    };

    const isNumericColumn = (colIdx, source = sortedBody) => {
        // Considera a coluna numérica se mais da metade dos valores não forem NaN
        let numericCount = 0;
        let total = 0;
        (source || []).forEach(row => {
            let val = getCellValue(row[colIdx]);
            if (val !== undefined && val !== null && val !== '') {
                total++;
                if (!isNaN(parseMaskedNumber(val)) && isFinite(parseMaskedNumber(val))) numericCount++;
            }
        });
        return total > 0 && numericCount / total > 0.5;
    };

    const handleSort = (colIdx) => {
        if (suppressSortClickRef.current) {
            suppressSortClickRef.current = false;
            return;
        }
        let direction = 'asc';
        if (sortConfig.col === colIdx) {
            if (sortConfig.direction === 'asc') direction = 'desc';
            else if (sortConfig.direction === 'desc') direction = null;
        }
        const newSortConfig = { col: direction ? colIdx : null, direction };
        setSortConfig(newSortConfig);
        setSortedBody(getProcessedBody(originalBody.current, filterConfig, newSortConfig));
    };

    const openFilterPopup = (colIdx, clientX, clientY) => {
        setFilterPopup({
            open: true,
            colIdx,
            x: clientX,
            y: clientY,
            value: filterConfig[colIdx] || ''
        });
    };

    const closeFilterPopup = () => {
        setFilterPopup(prev => ({ ...prev, open: false }));
    };

    const applyColumnFilter = () => {
        if (filterPopup.colIdx === null || filterPopup.colIdx === undefined) return;
        const value = String(filterPopup.value || '').trim();
        const nextFilters = { ...filterConfig };
        if (value) nextFilters[filterPopup.colIdx] = value;
        else delete nextFilters[filterPopup.colIdx];
        setFilterConfig(nextFilters);
        setSortedBody(getProcessedBody(originalBody.current, nextFilters, sortConfig));
        closeFilterPopup();
    };

    const clearColumnFilter = () => {
        if (filterPopup.colIdx === null || filterPopup.colIdx === undefined) return;
        const nextFilters = { ...filterConfig };
        delete nextFilters[filterPopup.colIdx];
        setFilterConfig(nextFilters);
        setSortedBody(getProcessedBody(originalBody.current, nextFilters, sortConfig));
        closeFilterPopup();
    };

    const startLongPress = (event, colIdx) => {
        const touch = event.touches && event.touches[0];
        if (!touch) return;
        longPressTimerRef.current = setTimeout(() => {
            suppressSortClickRef.current = true;
            openFilterPopup(colIdx, touch.clientX, touch.clientY);
        }, 2000);
    };

    const cancelLongPress = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    return (
        <>
            <table className="scroll table table-striped mt-2">
                {header && (
                    <thead>
                        <tr>
                            {header.map((col, idx) => {
                                const isSortable = col && typeof col === 'object' ? (col.sortable !== false && col.disabled !== true) : true;
                                return renderCell(
                                    col,
                                    idx,
                                    true,
                                    {
                                        onClick: () => handleSort(idx),
                                        onContextMenu: (event) => {
                                            event.preventDefault();
                                            openFilterPopup(idx, event.clientX, event.clientY);
                                        },
                                        onTouchStart: (event) => startLongPress(event, idx),
                                        onTouchEnd: cancelLongPress,
                                        onTouchMove: cancelLongPress
                                    },
                                    sortConfig.col === idx ? sortConfig.direction : null,
                                    !!filterConfig[idx],
                                    isSortable
                                );
                            })}
                        </tr>
                    </thead>
                )}
                {sortedBody && (
                    <tbody>
                        {sortedBody.map((row, rIdx) => (
                            <tr
                                key={rIdx}
                                onClick={() => onRowClick && onRowClick(rIdx, row)}
                                style={{ cursor: onRowClick ? 'pointer' : undefined }}
                            >
                                {row.map((cell, cIdx) => renderCell(cell, cIdx))}
                            </tr>
                        ))}
                    </tbody>
                )}
                {foot && (
                    <tfoot>
                        <tr>
                            {foot.map((col, idx) => renderCell(col, idx))}
                        </tr>
                    </tfoot>
                )}
            </table>

            {filterPopup.open && (
                <div
                    className="table-filter-popup"
                    style={{ top: filterPopup.y, left: filterPopup.x }}
                >
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={filterPopup.value}
                        autoFocus
                        placeholder="Filtrar coluna..."
                        onChange={(event) => setFilterPopup(prev => ({ ...prev, value: event.target.value }))}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') applyColumnFilter();
                            if (event.key === 'Escape') closeFilterPopup();
                        }}
                    />
                    <div className="table-filter-popup-actions">
                        <button type="button" className="btn btn-sm btn-primary" onClick={applyColumnFilter}>Aplicar</button>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={clearColumnFilter}>Limpar</button>
                        <button type="button" className="btn btn-sm btn-light" onClick={closeFilterPopup}>Fechar</button>
                    </div>
                </div>
            )}
        </>
    );
};

export default TABLE;

// Exemplo de uso com alinhamento correto no header, body e foot:
// function teste() {
//     return (
//         <TABLE
//             header={[
//                 '#',
//                 'Produto',
//                 'Descrição',
//                 { content: 'Unidade', className: 'text-center' },
//                 { content: 'Quantidade', className: 'text-end' },
//                 { content: 'Unitário', className: 'text-end' },
//                 { content: 'Total', className: 'text-end' }
//             ]}
//             body={itens.map((item, index) => [
//                 index + 1,
//                 item.codproduto,
//                 item.descricao,
//                 { content: item.unidade, className: 'text-center' },
//                 { content: formatNumber(item.quantidade), className: "text-end" },
//                 { content: formatNumber(item.vlrunitario), className: "text-end" },
//                 { content: formatNumber(item.totalitem, 4), className: "text-end" }
//             ])}
//             foot={[
//                 { content: <strong>Nro Itens: {formatNumber(itens.length, 0)}</strong>, className: "text-start", props: { colSpan: 2 } },
//                 { content: <strong>Total Nota:</strong>, className: "text-end", props: { colSpan: 3 } },
//                 { content: formatNumber(itens.reduce((acc, item) => acc + item.totalitem, 0), 2), className: "text-end" }
//             ]}
//         />
//     )
// }

// Exemplos de header com diferentes estilos:
// header={[
//     { content: '#', className: 'text-center' },
//     { content: 'Produto', className: 'text-start' },
//     { content: 'Valor', className: 'text-end bg-primary text-white' },
//     { content: 'Status', className: 'text-center', style: { backgroundColor: '#f0f0f0' } }
// ]}

// Forma alternativa (mantém compatibilidade com uso antigo):
// function testeCompatibilidade() {
//     return (
//         <TABLE
//             header={['#', 'Produto', 'Total']} // Ainda funciona normalmente
//             body={itens.map((item, index) => [
//                 index + 1,
//                 item.descricao,
//                 <span className="text-end">{formatNumber(item.totalitem)}</span> // Ainda funciona
//             ])}
//         />
//     )
// }   